import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import {
  FaBalanceScale,
  FaCalendarAlt,
  FaCapsules,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaEdit,
  FaMapMarkerAlt,
  FaPills,
  FaStickyNote,
  FaTag,
  FaTimes,
  FaTrash,
  FaUserMd,
} from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, ROLES, FEATURES } from "../../lib/rolesConfig";
import {
  commitFirestoreWrite,
  isOffline,
  offlineAwareMessage,
} from "../../utils/offline";

// Helper function to format Firestore Timestamp for date input fields (YYYY-MM-DD)
const formatDateForInput = (timestamp) => {
  if (timestamp && timestamp.toDate) {
    const date = timestamp.toDate();
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (timestamp instanceof Date) {
    const year = timestamp.getFullYear();
    const month = (timestamp.getMonth() + 1).toString().padStart(2, "0");
    const day = timestamp.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
};

const getTodayDateInput = () => formatDateForInput(new Date());

const FieldLabel = ({ icon: Icon, children }) => (
  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <Icon className="h-3 w-3" aria-hidden="true" />
    </span>
    <span>{children}</span>
  </label>
);

const fieldControlClass =
  "mt-2 block w-full rounded-xl border-gray-200 bg-gray-50/80 px-3 py-2 shadow-sm focus:border-blue-300 focus:ring-blue-200";

const ActiveMedicationsList = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [rawMedicationsList, setRawMedicationsList] = useState([]);
  const [sortedMedicationsList, setSortedMedicationsList] = useState([]);
  const [rawCompletedMedicationsList, setRawCompletedMedicationsList] =
    useState([]);
  const [sortedCompletedMedicationsList, setSortedCompletedMedicationsList] =
    useState([]);
  const [isLoadingActiveMedications, setIsLoadingActiveMedications] =
    useState(true);
  const [isLoadingCompletedMedications, setIsLoadingCompletedMedications] =
    useState(true);
  const [draftMedications, setDraftMedications] = useState([]);
  const [isLoadingDraftMedications, setIsLoadingDraftMedications] =
    useState(true);
  const [showDraftNudge, setShowDraftNudge] = useState(false);
  const [showAddMedicationForm, setShowAddMedicationForm] = useState(false);
  const [isEditingMedication, setIsEditingMedication] = useState(false);
  const [currentMedicationId, setCurrentMedicationId] = useState(null);
  const [sortConfig] = useState({ key: "medicationName", direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleMedsCount, setVisibleMedsCount] = useState(5);
  const [visibleCompletedMedsCount, setVisibleCompletedMedsCount] = useState(5);
  const cardRefs = useRef({});

  const initialNewMedicationState = {
    medicationName: "",
    genericName: "",
    form: "",
    strength: "",
    route: "",
    frequency: "",
    instructions: "",
    prescriber: "",
    startDate: "",
    endDate: "",
    notes: "",
    status: "Active",
  };
  const [newMedication, setNewMedication] = useState(initialNewMedicationState);

  const canWrite =
    activeCareProfile && userRole && currentUser
      ? hasPermission(
          userRole[activeCareProfile.id] ||
            (activeCareProfile.userId === currentUser.uid
              ? ROLES.OWNER
              : undefined),
          FEATURES.ACTIVE_PRN_MEDS,
          "write",
        )
      : false;

  useEffect(() => {
    if (!activeCareProfile) {
      setRawMedicationsList([]);
      setSortedMedicationsList([]);
      setIsLoadingActiveMedications(false);
      return;
    }
    setIsLoadingActiveMedications(true);
    const medicationsCollectionRef = collection(db, "medications");
    const q = query(
      medicationsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("status", "in", ["Active", "PRN"]),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedMeds = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRawMedicationsList(fetchedMeds);
        setIsLoadingActiveMedications(false);
      },
      (error) => {
        console.error("Error fetching active medications: ", error);
        if (!isOffline()) {
          window.alert("Failed to fetch active medications.");
        }
        setIsLoadingActiveMedications(false);
      },
    );
    return () => unsubscribe();
  }, [activeCareProfile]);

  useEffect(() => {
    if (!activeCareProfile) {
      setRawCompletedMedicationsList([]);
      setSortedCompletedMedicationsList([]);
      setIsLoadingCompletedMedications(false);
      return;
    }
    setIsLoadingCompletedMedications(true);
    const medicationsCollectionRef = collection(db, "medications");
    const q = query(
      medicationsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("status", "==", "Completed"),
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedMeds = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRawCompletedMedicationsList(fetchedMeds);
        setIsLoadingCompletedMedications(false);
      },
      (error) => {
        console.error("Error fetching completed medications: ", error);
        if (!isOffline()) {
          window.alert("Failed to fetch completed medications.");
        }
        setIsLoadingCompletedMedications(false);
      },
    );
    return () => unsubscribe();
  }, [activeCareProfile]);

  useEffect(() => {
    if (!activeCareProfile) {
      setDraftMedications([]);
      setIsLoadingDraftMedications(false);
      return;
    }

    setIsLoadingDraftMedications(true);
    const medicationsCollectionRef = collection(db, "medications");
    const q = query(
      medicationsCollectionRef,
      where("careProfileId", "==", activeCareProfile.id),
      where("status", "==", "Draft"),
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedDrafts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedDrafts.sort((a, b) => {
          const aTime = a?.lastModifiedAt?.toDate
            ? a.lastModifiedAt.toDate().getTime()
            : 0;
          const bTime = b?.lastModifiedAt?.toDate
            ? b.lastModifiedAt.toDate().getTime()
            : 0;
          return bTime - aTime;
        });
        setDraftMedications(fetchedDrafts);
        setIsLoadingDraftMedications(false);
      },
      (error) => {
        console.error("Error fetching draft medications: ", error);
        setDraftMedications([]);
        setIsLoadingDraftMedications(false);
      },
    );

    return () => unsubscribe();
  }, [activeCareProfile]);

  useEffect(() => {
    if (!activeCareProfile) {
      setShowDraftNudge(false);
      return;
    }
    if (!draftMedications || draftMedications.length === 0) {
      setShowDraftNudge(false);
      return;
    }

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const dayKey = `${yyyy}-${mm}-${dd}`;
    const storageKey = `mila:draftMedsNudgeShown:${activeCareProfile.id}:${dayKey}`;

    try {
      const wasShown = window.localStorage.getItem(storageKey) === "1";
      if (wasShown) {
        setShowDraftNudge(false);
        return;
      }
      window.localStorage.setItem(storageKey, "1");
      setShowDraftNudge(true);
    } catch (e) {
      // localStorage may not be available; fail open but don't crash
      setShowDraftNudge(true);
    }
  }, [activeCareProfile, draftMedications]);

  useEffect(() => {
    // If another screen asked us to open a specific draft (e.g., Logs CTA), do it once.
    if (!activeCareProfile) return;
    if (!draftMedications || draftMedications.length === 0) return;

    const pendingKey = `mila:pendingFinishDraftMed:${activeCareProfile.id}`;
    try {
      const pendingDraftId = window.localStorage.getItem(pendingKey);
      if (!pendingDraftId) return;

      const draft = draftMedications.find((d) => d.id === pendingDraftId);
      if (!draft) return;

      window.localStorage.removeItem(pendingKey);
      handleFinishDraftSetup(draft);
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCareProfile, draftMedications]);

  useEffect(() => {
    const processMedicationList = (medications) => {
      let itemsToProcess = [...medications];

      if (searchTerm.trim() !== "") {
        itemsToProcess = itemsToProcess.filter(
          (med) =>
            med.medicationName &&
            med.medicationName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }

      if (sortConfig.key !== null) {
        itemsToProcess.sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];

          const extractNumber = (str) => {
            if (typeof str === "string") {
              const match = str.match(/^(\d*\.?\d+)/);
              return match ? parseFloat(match[1]) : null;
            }
            return null;
          };

          if (sortConfig.key === "strength") {
            const aNum = extractNumber(aValue);
            const bNum = extractNumber(bValue);
            if (aNum !== null && bNum !== null) {
              aValue = aNum;
              bValue = bNum;
            } else if (aNum !== null) {
              return -1;
            } else if (bNum !== null) {
              return 1;
            }
          } else if (
            sortConfig.key === "startDate" ||
            sortConfig.key === "endDate"
          ) {
            if (aValue && aValue.toDate) aValue = aValue.toDate();
            if (bValue && bValue.toDate) bValue = bValue.toDate();
          }

          if (aValue === null || aValue === undefined)
            aValue = sortConfig.direction === "asc" ? Infinity : -Infinity;
          if (bValue === null || bValue === undefined)
            bValue = sortConfig.direction === "asc" ? Infinity : -Infinity;

          if (
            !(aValue instanceof Date) &&
            (aValue === Infinity || aValue === -Infinity) &&
            sortConfig.key !== "startDate" &&
            sortConfig.key !== "endDate"
          )
            aValue = "";
          if (
            !(bValue instanceof Date) &&
            (bValue === Infinity || bValue === -Infinity) &&
            sortConfig.key !== "startDate" &&
            sortConfig.key !== "endDate"
          )
            bValue = "";

          if (typeof aValue === "string" && typeof bValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (aValue < bValue) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        });
      }

      return itemsToProcess;
    };

    setSortedMedicationsList(processMedicationList(rawMedicationsList));
    setSortedCompletedMedicationsList(
      processMedicationList(rawCompletedMedicationsList),
    );
  }, [rawMedicationsList, rawCompletedMedicationsList, sortConfig, searchTerm]);

  useEffect(() => {
    setVisibleMedsCount(5);
    setVisibleCompletedMedsCount(5);
  }, [searchTerm, sortConfig]);

  const handleLoadMore = () => {
    setVisibleMedsCount((prevCount) => prevCount + 5);
  };

  const handleLoadMoreCompleted = () => {
    setVisibleCompletedMedsCount((prevCount) => prevCount + 5);
  };

  const handleCancelEdit = () => {
    const idToScrollTo = currentMedicationId;

    setShowAddMedicationForm(false);
    setIsEditingMedication(false);
    setCurrentMedicationId(null);
    setNewMedication(initialNewMedicationState);

    if (idToScrollTo) {
      setTimeout(() => {
        const cardElement = cardRefs.current[idToScrollTo];
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMedication((prevState) => ({ ...prevState, [name]: value }));
  };

  const getCurrentActor = () => ({
    uid: currentUser.uid,
    name: currentUser.displayName || currentUser.email,
  });

  const handleFinishDraftSetup = (draftMed) => {
    if (!canWrite) {
      window.alert("You don't have permission to edit medications.");
      return;
    }

    setIsEditingMedication(true);
    setCurrentMedicationId(draftMed.id);

    // Open the full form, defaulting Draft -> Active so the user can complete required fields.
    setNewMedication({
      ...draftMed,
      status: "Active",
      startDate: formatDateForInput(draftMed.startDate),
      endDate: draftMed.endDate ? formatDateForInput(draftMed.endDate) : "",
      genericName: draftMed.genericName || "",
      form: draftMed.form || "",
      strength: draftMed.strength || "",
      route: draftMed.route || "",
      frequency: draftMed.frequency || "",
      instructions: draftMed.instructions || "",
      prescriber: draftMed.prescriber || "",
      notes: draftMed.notes || "",
    });
    setShowAddMedicationForm(true);

    setTimeout(() => {
      document
        .getElementById("medication-form-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDeleteDraftMedication = async (id) => {
    if (!canWrite) {
      window.alert("You don't have permission to delete medications.");
      return;
    }
    if (!window.confirm("Delete this draft medication?")) return;

    try {
      await commitFirestoreWrite(
        deleteDoc(doc(db, "medications", id)),
        "Delete draft medication",
      );
    } catch (error) {
      console.error("Error deleting draft medication: ", error);
      window.alert("Failed to delete draft medication.");
    }
  };

  const handleMedicationSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      window.alert("You do not have permission to perform this action.");
      return;
    }
    if (!activeCareProfile) {
      window.alert("No care profile selected.");
      return;
    }
    if (
      !newMedication.medicationName ||
      !newMedication.strength ||
      !newMedication.form ||
      !newMedication.route ||
      !newMedication.frequency ||
      !newMedication.startDate ||
      !newMedication.status
    ) {
      window.alert(
        "Please fill in all required fields: Name, Strength, Form, Route, Frequency, Start Date, Status.",
      );
      return;
    }

    const lastModifiedBy = {
      uid: currentUser.uid,
      name: currentUser.displayName || currentUser.email,
    };

    const medicationPayload = {
      ...newMedication,
      startDate: Timestamp.fromDate(new Date(newMedication.startDate)),
      endDate: newMedication.endDate
        ? Timestamp.fromDate(new Date(newMedication.endDate))
        : null,
      completedAt:
        newMedication.status === "Completed"
          ? newMedication.completedAt || Timestamp.now()
          : null,
      careProfileId: activeCareProfile.id,
      lastModifiedAt: Timestamp.now(),
      lastModifiedBy: lastModifiedBy,
    };

    if (isEditingMedication && currentMedicationId) {
      try {
        const medDocRef = doc(db, "medications", currentMedicationId);
        await commitFirestoreWrite(
          updateDoc(medDocRef, medicationPayload),
          "Update medication",
        );
        window.alert(offlineAwareMessage("Medication updated successfully!"));
      } catch (error) {
        console.error("Error updating medication: ", error);
        window.alert("Failed to update medication.");
      }
    } else {
      medicationPayload.userId = activeCareProfile.userId;
      medicationPayload.createdAt = Timestamp.now();
      medicationPayload.createdBy = lastModifiedBy;
      try {
        const medicationRef = doc(collection(db, "medications"));
        await commitFirestoreWrite(
          setDoc(medicationRef, medicationPayload),
          "Create medication",
        );
        window.alert(offlineAwareMessage("Medication added successfully!"));
      } catch (error) {
        console.error("Error adding medication: ", error);
        window.alert("Failed to add medication.");
      }
    }
    handleCancelEdit();
  };

  const handleEditMedication = (med) => {
    if (!canWrite) {
      window.alert("You don't have permission to edit medications.");
      return;
    }
    setIsEditingMedication(true);
    setCurrentMedicationId(med.id);
    setNewMedication({
      ...med,
      status: med.status === "Draft" ? "Active" : med.status,
      startDate: formatDateForInput(med.startDate),
      endDate: med.endDate ? formatDateForInput(med.endDate) : "",
    });
    setShowAddMedicationForm(true);
    setTimeout(() => {
      document
        .getElementById("medication-form-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleMarkMedicationComplete = async (med) => {
    if (!canWrite) {
      window.alert("You don't have permission to edit medications.");
      return;
    }
    if (!med?.id) return;
    if (
      !window.confirm(
        `Mark ${med.medicationName || "this medication"} as completed? It will stop appearing in scheduled reminders.`,
      )
    ) {
      return;
    }

    const now = Timestamp.now();
    try {
      await commitFirestoreWrite(
        updateDoc(doc(db, "medications", med.id), {
          status: "Completed",
          completedAt: now,
          lastModifiedAt: now,
          lastModifiedBy: getCurrentActor(),
        }),
        "Mark medication complete",
      );
      window.alert(offlineAwareMessage("Medication marked as completed."));
      if (med.id === currentMedicationId) {
        handleCancelEdit();
      }
    } catch (error) {
      console.error("Error completing medication: ", error);
      window.alert("Failed to mark medication as completed.");
    }
  };

  const handleReactivateMedication = (med) => {
    if (!canWrite) {
      window.alert("You don't have permission to edit medications.");
      return;
    }
    if (!med?.id) return;

    setIsEditingMedication(true);
    setCurrentMedicationId(med.id);
    setNewMedication({
      ...med,
      status: "Active",
      startDate: getTodayDateInput(),
      endDate: "",
      genericName: med.genericName || "",
      form: med.form || "",
      strength: med.strength || "",
      route: med.route || "",
      frequency: med.frequency || "",
      instructions: med.instructions || "",
      prescriber: med.prescriber || "",
      notes: med.notes || "",
    });
    setShowAddMedicationForm(true);
    setTimeout(() => {
      document
        .getElementById("medication-form-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDeleteMedication = async (id) => {
    if (!canWrite) {
      window.alert("You don't have permission to delete medications.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this medication?")) {
      try {
        await commitFirestoreWrite(
          deleteDoc(doc(db, "medications", id)),
          "Delete medication",
        );
        window.alert(offlineAwareMessage("Medication deleted successfully."));
        if (id === currentMedicationId) {
          handleCancelEdit();
        }
      } catch (error) {
        console.error("Error deleting medication: ", error);
        window.alert("Failed to delete medication.");
      }
    }
  };

  const toggleAddMedicationForm = () => {
    setIsEditingMedication(false);
    setCurrentMedicationId(null);
    setNewMedication(initialNewMedicationState);
    setShowAddMedicationForm(!showAddMedicationForm);
    setTimeout(() => {
      document
        .getElementById("medication-form-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  if (isLoadingActiveMedications) {
    return <div className="text-center p-4">Loading active medications...</div>;
  }

  if (!activeCareProfile) {
    return (
      <div className="text-center p-4 text-red-500">
        Please select a care profile to view medications.
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      {showDraftNudge && draftMedications.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-sm">
              You have{" "}
              <span className="font-semibold">{draftMedications.length}</span>{" "}
              draft medicine{draftMedications.length === 1 ? "" : "s"} to finish
              setting up.
            </div>
            <button
              type="button"
              onClick={() => {
                document
                  .getElementById("draft-medicines-section")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="w-full md:w-auto rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
            >
              Review drafts
            </button>
          </div>
        </div>
      )}

      <div
        id="draft-medicines-section"
        className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Draft medicines
            </h3>
            <p className="text-sm text-gray-600">
              Drafts are created from Logs (“+ Add new medicine…”). Finish setup
              here to add them to Active/PRN meds.
            </p>
          </div>
        </div>

        {isLoadingDraftMedications ? (
          <div className="mt-3 text-sm text-gray-500">Loading drafts...</div>
        ) : draftMedications.length > 0 ? (
          <div className="mt-4 space-y-3">
            {draftMedications.map((med) => (
              <div
                key={med.id}
                className="flex flex-col gap-3 rounded-lg border bg-white p-3 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-semibold text-gray-900">
                    {med.medicationName}
                    <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      Draft
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Finish setup to add this to Active/PRN meds.
                  </div>
                </div>

                {canWrite && (
                  <div className="flex flex-col gap-2 md:flex-row">
                    <button
                      type="button"
                      onClick={() => handleFinishDraftSetup(med)}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                    >
                      Finish setup
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteDraftMedication(med.id)}
                      className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 text-sm text-gray-500 italic">
            No draft medicines yet.
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-700 md:order-1">
          Active & PRN Medications
        </h2>

        {canWrite && (
          <button
            onClick={toggleAddMedicationForm}
            className="w-full md:w-auto px-4 py-2 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 transition-all md:order-3"
          >
            {showAddMedicationForm ? "Cancel" : "Add New Medication"}
          </button>
        )}

        <div className="relative w-full md:w-1/3 md:order-2">
          <input
            type="text"
            placeholder="Search medications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 pl-8 border rounded-lg"
          />
          <svg
            className="w-5 h-5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {showAddMedicationForm && (
        <div
          id="medication-form-section"
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-3 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="medication-form-title"
        >
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-4 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-100 text-gray-600">
                  <FaCapsules className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h3
                    id="medication-form-title"
                    className="text-xl font-semibold text-gray-900"
                  >
                    {isEditingMedication
                      ? "Edit Medication"
                      : "Add New Medication"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Fill in the medicine details without losing your place in
                    the list.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="rounded-xl border border-gray-300 p-2 text-gray-600 hover:bg-gray-50"
                aria-label="Close medication form"
              >
                <FaTimes />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-6 sm:p-8">
              <form
                onSubmit={handleMedicationSubmit}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                <div className="lg:col-span-1">
                  <FieldLabel icon={FaCapsules}>Medication Name*</FieldLabel>
                  <input
                    type="text"
                    name="medicationName"
                    value={newMedication.medicationName}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    required
                  />
                </div>
                <div>
                  <FieldLabel icon={FaTag}>Generic Name</FieldLabel>
                  <input
                    type="text"
                    name="genericName"
                    value={newMedication.genericName}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                  />
                </div>
                <div>
                  <FieldLabel icon={FaBalanceScale}>Strength*</FieldLabel>
                  <input
                    type="text"
                    name="strength"
                    value={newMedication.strength}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    placeholder="e.g., 500 mg"
                    required
                  />
                </div>
                <div>
                  <FieldLabel icon={FaPills}>Form*</FieldLabel>
                  <input
                    type="text"
                    name="form"
                    value={newMedication.form}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    placeholder="e.g., Tablet, Capsule"
                    required
                  />
                </div>
                <div>
                  <FieldLabel icon={FaMapMarkerAlt}>Route*</FieldLabel>
                  <input
                    type="text"
                    name="route"
                    value={newMedication.route}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    placeholder="e.g., Oral"
                    required
                  />
                </div>
                <div>
                  <FieldLabel icon={FaClock}>Frequency*</FieldLabel>
                  <input
                    type="text"
                    name="frequency"
                    value={newMedication.frequency}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    placeholder="e.g., Once daily"
                    required
                  />
                </div>
                <div className="lg:col-span-3">
                  <FieldLabel icon={FaClipboardList}>Instructions</FieldLabel>
                  <textarea
                    name="instructions"
                    value={newMedication.instructions}
                    onChange={handleInputChange}
                    rows="2"
                    className={fieldControlClass}
                  ></textarea>
                </div>
                <div>
                  <FieldLabel icon={FaUserMd}>Prescriber</FieldLabel>
                  <input
                    type="text"
                    name="prescriber"
                    value={newMedication.prescriber}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                  />
                </div>
                <div>
                  <FieldLabel icon={FaCalendarAlt}>Start Date*</FieldLabel>
                  <input
                    type="date"
                    name="startDate"
                    value={newMedication.startDate}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    required
                  />
                </div>
                <div>
                  <FieldLabel icon={FaCalendarAlt}>End Date</FieldLabel>
                  <input
                    type="date"
                    name="endDate"
                    value={newMedication.endDate}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                  />
                </div>
                <div className="lg:col-span-2">
                  <FieldLabel icon={FaCheckCircle}>Status*</FieldLabel>
                  <select
                    name="status"
                    value={newMedication.status}
                    onChange={handleInputChange}
                    className={fieldControlClass}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="PRN">PRN (As Needed)</option>
                    {isEditingMedication && (
                      <option value="Completed">Completed</option>
                    )}
                  </select>
                </div>
                <div className="lg:col-span-3">
                  <FieldLabel icon={FaStickyNote}>Notes</FieldLabel>
                  <textarea
                    name="notes"
                    value={newMedication.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className={fieldControlClass}
                  ></textarea>
                </div>
                <div className="lg:col-span-3 flex flex-col md:flex-row md:justify-end space-y-4 md:space-y-0 md:space-x-4 mt-4">
                  {isEditingMedication && newMedication.status === "Active" && (
                    <button
                      type="button"
                      onClick={() =>
                        handleMarkMedicationComplete({
                          ...newMedication,
                          id: currentMedicationId,
                        })
                      }
                      className="w-full md:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Mark complete
                    </button>
                  )}
                  <button
                    type="submit"
                    className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {isEditingMedication
                      ? "Update Medication"
                      : "Save Medication"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="w-full md:w-auto px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {sortedMedicationsList.length > 0 ? (
        <div>
          <div className="space-y-4">
            {sortedMedicationsList.slice(0, visibleMedsCount).map((med) => (
              <div
                key={med.id}
                ref={(el) => (cardRefs.current[med.id] = el)}
                className="bg-blue-50 p-4 rounded-lg shadow-sm border border-gray-200 transition-shadow hover:shadow-md relative"
              >
                <div className="grid grid-cols-3 gap-4 mb-2">
                  <div className="col-span-2">
                    <h3 className="font-bold text-lg text-blue-800">
                      {med.medicationName}
                      <span className="text-gray-500 font-normal text-sm italic">
                        {" "}
                        ({med.genericName})
                      </span>
                    </h3>
                    <p className="text-sm text-gray-700">
                      {med.strength} {med.form}
                    </p>
                  </div>
                </div>

                <div className="text-sm text-gray-800 space-y-1 mb-4">
                  <p>
                    <span className="font-semibold">Frequency:</span>{" "}
                    {med.frequency}
                  </p>
                  <p>
                    <span className="font-semibold">Route:</span> {med.route}
                  </p>
                  <p>
                    <span className="font-semibold">Prescriber:</span>{" "}
                    {med.prescriber}
                  </p>
                  <p>
                    <span className="font-semibold">Start Date:</span>{" "}
                    {formatDateForInput(med.startDate)}
                  </p>
                  <p>
                    <span className="font-semibold">End Date:</span>{" "}
                    {med.endDate ? formatDateForInput(med.endDate) : "Ongoing"}
                  </p>
                  <p>
                    <span className="font-semibold">Instructions:</span>{" "}
                    {med.instructions}
                  </p>
                  <p>
                    <span className="font-semibold">Notes:</span> {med.notes}
                  </p>
                </div>

                {/* Action Buttons Footer */}
                {canWrite && (
                  <div className="border-t border-gray-200 pt-3 flex flex-wrap justify-end items-center gap-2 md:border-none md:pt-0 md:absolute md:top-4 md:right-4">
                    {med.status === "Active" && (
                      <button
                        type="button"
                        onClick={() => handleMarkMedicationComplete(med)}
                        className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-200 hover:bg-green-50"
                        title="Mark medication as completed"
                      >
                        Mark complete
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEditMedication(med)}
                      className="p-2 text-yellow-600 hover:text-yellow-800"
                      title="Edit Medication"
                    >
                      <FaEdit size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteMedication(med.id)}
                      className="p-2 text-red-600 hover:text-red-800"
                      title="Delete Medication"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                )}

                {/* Audit Trail */}
                <div className="text-xs text-gray-500 italic mt-4">
                  {med.lastModifiedBy && med.lastModifiedBy.uid !== med.userId
                    ? `Last updated by ${med.lastModifiedBy.name} on ${new Date(med.lastModifiedAt.toDate()).toLocaleString()}`
                    : med.createdAt
                      ? `Created on ${new Date(med.createdAt.toDate()).toLocaleString()}`
                      : `Last updated on ${new Date(med.lastModifiedAt.toDate()).toLocaleString()}`}
                </div>
              </div>
            ))}
          </div>
          {visibleMedsCount < sortedMedicationsList.length && (
            <div className="text-center mt-6">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center p-4 text-gray-500 italic">
          No active or PRN medications found.
        </div>
      )}

      <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Completed medications
            </h3>
            <p className="text-sm text-gray-600">
              Medicines saved for reuse. Reactivate one when it is prescribed
              again.
            </p>
          </div>
        </div>

        {isLoadingCompletedMedications ? (
          <div className="mt-3 text-sm text-gray-500">
            Loading completed medications...
          </div>
        ) : sortedCompletedMedicationsList.length > 0 ? (
          <div className="mt-4 space-y-3">
            {sortedCompletedMedicationsList
              .slice(0, visibleCompletedMedsCount)
              .map((med) => (
                <div
                  key={med.id}
                  className="flex flex-col gap-3 rounded-lg border bg-white p-3 md:flex-row md:items-start md:justify-between"
                >
                  <div>
                    <div className="font-semibold text-gray-900">
                      {med.medicationName}
                      <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        Completed
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-700">
                      {[med.strength, med.form].filter(Boolean).join(" ")}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {med.frequency
                        ? `Previous frequency: ${med.frequency}`
                        : "Reactivate to set the latest schedule."}
                    </div>
                  </div>

                  {canWrite && (
                    <div className="flex flex-col gap-2 md:flex-row">
                      <button
                        type="button"
                        onClick={() => handleReactivateMedication(med)}
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                      >
                        Reactivate
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditMedication(med)}
                        className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              ))}

            {visibleCompletedMedsCount <
              sortedCompletedMedicationsList.length && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={handleLoadMoreCompleted}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Load More Completed
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3 text-sm text-gray-500 italic">
            No completed medications yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveMedicationsList;
